import {Component} from 'panel';

import {ValidationStatus} from 'common/components/input/types';
import {parseNumber} from 'common/util';
import {ENTER_KEY_CODE} from 'common/util/dom';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {SECONDS_BY_UNIT} from 'common/util/date';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import template from './index.jade';
import css from './index.styl?inline';

interface BucketPaneState {
  tooltipErrorMsg: string;
  editedUnit: string;
  editedBucket: Bucket;
}

function firstValidationError(validationFns: Array<() => string>): string {
  for (const validationFn of validationFns) {
    const errorMessage = validationFn();
    if (errorMessage) {
      return errorMessage;
    }
  }
  return null;
}

/** widget for selecting bucket size, min, and max. */
export class BucketPane extends Component<BucketPaneState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      units: {
        type: `json`,
        description: `interface: string[]`,
      },
      'selected-bucket': {type: `json`, description: `Currently selected bucket`},
      'selected-unit': {type: `string`, description: `Currently selected unit`},
      'reset-bucket': {
        type: `boolean`,
        description: `whether to display reset button`,
      },
      'max-num-buckets': {
        type: `number`,
        default: 90,
        description: `provide a max number of buckets allowed for error handling`,
      },
      'range-min': {
        type: `number`,
        default: null,
        description: `if specified, the minimum value needed to pass validation`,
      },
      header: {
        type: `string`,
        default: `Define Range`,
        description: `header text`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        tooltipErrorMsg: null,
        editedUnit: null,
        editedBucket: null,
      },
      helpers: {
        applyBucketSelections: () => {
          this.dispatchEvent(
            new CustomEvent(`changeSelectedBucket`, {
              detail: {
                bucketSize: this.helpers.selectedUnitToVal(this.state.editedBucket.bucketSize, this.state.editedUnit),
                max: this.helpers.selectedUnitToVal(this.state.editedBucket.max, this.state.editedUnit),
                min: this.helpers.selectedUnitToVal(this.state.editedBucket.min, this.state.editedUnit),
                offset: this.state.editedBucket.offset,
              },
            }),
          );
        },
        changeBucketSize: (ev) => {
          const editedBucket = {...this.state.editedBucket, bucketSize: parseNumber(ev.target.value)};
          this.update({tooltipErrorMsg: this.validate(editedBucket), editedBucket});
        },
        changeBucketUnit: (ev) => {
          const {value: newUnit, isSelected} = ev.detail.item;
          if (!isSelected) {
            this.update({
              editedUnit: newUnit,
            });
          }
        },
        changeBucketMax: (ev) => {
          const editedBucket = {...this.state.editedBucket, max: parseNumber(ev.target.value)};
          this.update({tooltipErrorMsg: this.validate(editedBucket), editedBucket});
        },
        changeBucketMin: (ev) => {
          const editedBucket = {...this.state.editedBucket, min: parseNumber(ev.target.value)};
          this.update({tooltipErrorMsg: this.validate(editedBucket), editedBucket});
        },
        resetInterval: () => {
          this.dispatchEvent(new CustomEvent(`resetBucket`, {detail: null}));
        },
        inputInserted: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.focus();
          }),
        canSelectBucketUnit: () => {
          return this.attr(`units`) && this.attr(`units`).length > 1;
        },
        getBucketUnits: () => {
          const units = this.helpers.canSelectBucketUnit() ? this.attr(`units`) : [this.attr(`selected-unit`)];
          return [
            {
              items: units.map((unit) => ({
                value: unit,
                label: unit,
                isSelected: unit === this.state.editedUnit,
              })),
            },
          ];
        },
        getBucketSize: () => {
          return this.state.editedBucket.bucketSize;
        },
        getMax: () => {
          return this.state.editedBucket.max;
        },
        getMin: () => {
          return this.state.editedBucket.min;
        },
        getInputValidationStatus: (errorMessage: string) => {
          return errorMessage ? ValidationStatus.Invalid : ValidationStatus.Default;
        },
        getBucketSizeValidationStatus: () => {
          return this.helpers.getInputValidationStatus(this.validateBucketSize(this.helpers.getBucketSize()));
        },
        getMaxValidationStatus: () => {
          return this.helpers.getInputValidationStatus(this.validateMax(this.helpers.getMax()));
        },
        getMinValidationStatus: () => {
          return this.helpers.getInputValidationStatus(this.validateMin(this.helpers.getMin()));
        },
        /** Transforms if unit is in default transform eg Minutes, Hours, Days */
        valToSelectedUnit: (val: number, unit: string) => {
          if (!Object.prototype.hasOwnProperty.call(SECONDS_BY_UNIT, unit)) {
            return val;
          }
          return val / SECONDS_BY_UNIT[unit];
        },
        selectedUnitToVal: (unitVal: number, unit: string) => {
          if (!Object.prototype.hasOwnProperty.call(SECONDS_BY_UNIT, unit)) {
            return unitVal;
          }
          return unitVal * SECONDS_BY_UNIT[unit];
        },
        submitDisabled: () => {
          return Boolean(this.state.tooltipErrorMsg);
        },
        handleKeyUp: (ev) => {
          if (ev.keyCode === ENTER_KEY_CODE && !this.helpers.submitDisabled()) {
            this.helpers.applyBucketSelections();
          }
        },
      },
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    const bucket = this.attr(`selected-bucket`);
    const unit = this.attr(`selected-unit`);
    if (!bucket || !unit) {
      return;
    }

    this.update({
      editedUnit: unit,
      editedBucket: {
        bucketSize: this.helpers.valToSelectedUnit(bucket.bucketSize, unit),
        max: this.helpers.valToSelectedUnit(bucket.max, unit),
        min: this.helpers.valToSelectedUnit(bucket.min, unit),
        offset: bucket.offset,
      },
    });
  }

  validateNumber({
    fieldName,
    number,
    inclusiveMin,
    exclusiveMin,
  }: {
    fieldName: string;
    number: number;
    inclusiveMin?: number;
    exclusiveMin?: number;
  }): string {
    return firstValidationError([
      () => (!Number.isFinite(number) ? `${fieldName} is not specified` : null),
      () => (Number.isFinite(inclusiveMin) && number < inclusiveMin ? `${fieldName} must be >= ${inclusiveMin}` : null),
      () => (Number.isFinite(exclusiveMin) && number <= exclusiveMin ? `${fieldName} must be > ${exclusiveMin}` : null),
    ]);
  }

  validateBucketSize(bucketSize: number): string {
    return this.validateNumber({fieldName: `Interval Size`, number: bucketSize, exclusiveMin: 0});
  }

  validateMax(max: number): string {
    return this.validateNumber({fieldName: `Maximum`, number: max});
  }

  validateMin(min: number): string {
    return this.validateNumber({fieldName: `Minimum`, number: min, inclusiveMin: this.attr(`range-min`)});
  }

  validate(bucket: Bucket): string {
    const bucketDifference = () => bucket.max - bucket.min;

    return firstValidationError([
      () => this.validateBucketSize(bucket.bucketSize),
      () => this.validateMin(bucket.min),
      () => this.validateMax(bucket.max),
      () => (bucketDifference() < 0 ? `Invalid interval: Max must be greater than Min` : null),
      () =>
        bucketDifference() / bucket.bucketSize > this.attr(`max-num-buckets`)
          ? `Invalid interval: Number of intervals cannot exceed ${this.attr(`max-num-buckets`)}`
          : null,
      () =>
        bucket.bucketSize > bucketDifference()
          ? `Invalid interval: interval cannot be greater than the given range`
          : null,
    ]);
  }
}

export const BucketPaneEl = defineAndMakeJsxFactory(`mp-bucket-pane`, BucketPane);
